import type { TestGroupingItem } from '@vedalib/editor/lib/elements'
import { useCallback } from 'react'
import React from 'react'

import PanelIconImage from 'components/Editor/FloatingPanel/PanelIconImage'
import { IRenderItemProps } from 'components/uiKit/ArrayControl'
import { IconButton, TileButton } from 'components/uiKit/Button'
import { Tooltip } from 'components/uiKit/Dropdown'
import { KitSize } from 'components/uiKit/KitTypes'
import { Typography } from 'components/uiKit/Typography'
import { setElement } from 'services/Store/Project/actions'
import { useProjectContext, useProjectDispatch } from 'services/Store/Project/hooks'
import { getActiveElementId } from 'services/Store/Project/selectors'
import { t } from 'services/Translation'

import {
  useGroupingFormValueSetElementState,
  useGroupingFormValueUpdateForm,
} from './TestGroupingFormValueContext'
import * as s from './TestGroupingFormValueItemAnswer.module.scss'
import type { TestGroupingStateType } from './testGroupingTypes'

const TestGroupingFormValueItemAnswer: React.FC<IRenderItemProps<TestGroupingItem>> = ({
  name,
  index,
  item,
  min,
  value: array,
  onChange,
}) => {
  const dispatch = useProjectDispatch()
  const elementId = useProjectContext(getActiveElementId)
  const disableDelete = Boolean(min && array.length <= min)
  const setForm = useGroupingFormValueUpdateForm()
  const setElementState = useGroupingFormValueSetElementState()

  const setState = useCallback(
    (value: TestGroupingStateType) => {
      if (elementId) {
        dispatch(setElement({ id: elementId, value }))
      }
    },
    [dispatch, elementId],
  )

  const handleFocus = useCallback(() => {
    setState({ activeAnswer: item.value, activeGroup: null })
  }, [setState, item.value])

  const handleBlur = useCallback(() => setState({ activeAnswer: null }), [setState])

  const onDelete = useCallback(
    () =>
      onChange?.(
        array.filter((_item, i) => i !== index),
        { command: 'delete', item: array[index] },
      ),
    [onChange, array, index],
  )

  const handleSetAnswer = useCallback(() => {
    setForm({ form: 'answer', answerName: name, answerIndex: index })
    setElementState?.({ answersOrder: [item.value] })
  }, [setForm, index, name, setElementState, item.value])

  return (
    <div className={s.root} onBlur={handleBlur} onFocus={handleFocus}>
      <div className={s.header}>
        <span className={s.title}>
          {t('elements.grouping.form.answer')} {index + 1}
        </span>
        <span className={s.actions}>
          <Tooltip overlay={t('uiKit.tooltip.delete')}>
            <IconButton
              disabled={disableDelete}
              icon='otherTrash'
              name={`${name}.delete`}
              onClick={onDelete}
              size={KitSize.XS}
              styleType='ghost'
            />
          </Tooltip>
        </span>
      </div>
      <TileButton icon='otherAdd' name={`${name}.open`} onClick={handleSetAnswer} styleType='ghost'>
        <PanelIconImage file={item.image} icon='otherBlock' />
        <Typography rows={1} styleType='hint'>
          {t('elements.grouping.tags.answer')}
        </Typography>
      </TileButton>
    </div>
  )
}

export default React.memo(TestGroupingFormValueItemAnswer) as typeof TestGroupingFormValueItemAnswer
